/**
 * `/ops/billing` — FinderlyFix admin: every organisation with a billing
 * account (contractor pages phase 2), from `GET /partner/ops/billing/summary`.
 * Sorted by balance (largest owed first; the column header flips it). Each
 * row opens the organisation's Billing section on its detail page
 * (`/ops/partners/:id#billing`, billing/OpsBillingPanel.tsx), where the
 * account, entries and invoices are changed.
 */
import { useState } from 'react';
import type { OpsBillingSummaryRow } from '../api/types';
import { useSession } from '../auth/session';
import { billingError } from '../billing/errors';
import { formatCents } from '../billing/money';
import { Amount } from '../billing/parts';
import { Link } from '../router';
import { Empty, ErrorNotice, Loading, PageHead } from '../ui/kit';
import { useQuery, usePhone } from '../ui/hooks';

type Sort = 'desc' | 'asc';

function detailPath(row: OpsBillingSummaryRow): string {
  return `/ops/partners/${encodeURIComponent(row.partner_id)}#billing`;
}

export function OpsBilling() {
  const { api } = useSession();
  const summary = useQuery(() => api.opsBillingSummary(), ['ops-billing-summary']);
  const [sort, setSort] = useState<Sort>('desc');
  const phone = usePhone();
  const rows = [...(summary.data?.partners ?? [])].sort((a, b) =>
    sort === 'desc' ? b.balance_cents - a.balance_cents : a.balance_cents - b.balance_cents,
  );
  const owed = rows.reduce((n, r) => n + Math.max(0, r.balance_cents), 0);
  const uninvoiced = rows.reduce((n, r) => n + r.uninvoiced_cents, 0);

  return (
    <div>
      <PageHead
        title="Billing (admin)"
        eyebrow="FinderlyFix admin"
        lede="Organisations with a billing account: what each owes, what is not invoiced yet and any open disputes."
        actions={
          <Link className="btn btn-sm" to="/ops/partners">
            All partners
          </Link>
        }
      />
      <section className="panel" aria-labelledby="ops-billing-summary-h">
        <div className="panel-head">
          <h2 id="ops-billing-summary-h">Billing accounts</h2>
          {rows.length ? (
            <span className="small muted">
              {formatCents(owed)} owed · {formatCents(uninvoiced)} not invoiced
            </span>
          ) : null}
        </div>
        {summary.loading && !summary.data ? (
          <Loading label="Loading billing accounts" />
        ) : summary.error?.status === 404 ? (
          <Empty level={3} title="Contractor billing is off">
            The server’s billing switch is off, so no organisation is billed.
          </Empty>
        ) : summary.error ? (
          <div className="panel-body">
            <ErrorNotice error={billingError(summary.error)} title="Could not load billing accounts" />
          </div>
        ) : !rows.length ? (
          <Empty level={3} title="Nothing to review">
            No organisation is enrolled in contractor billing yet. Enrol one from its page under All partners.
          </Empty>
        ) : phone ? (
          <ul className="cards" aria-label="Billing accounts" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {rows.map((r) => (
              <li key={r.partner_id} className="card ops-card">
                <Link to={detailPath(r)} className="ops-strong">
                  {r.display_name}
                </Link>
                <span>
                  Balance <Amount cents={r.balance_cents} /> · {formatCents(r.uninvoiced_cents)} not invoiced
                </span>
                <span className="small muted">
                  Lead fee {formatCents(r.lead_fee_cents)} · fees {r.fees_enabled ? 'on' : 'off'}
                  {r.open_disputes ? ` · ${r.open_disputes} open dispute${r.open_disputes === 1 ? '' : 's'}` : ''}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="table-wrap">
            <table className="data ops-billing-table">
              <thead>
                <tr>
                  <th scope="col">Organisation</th>
                  <th scope="col" className="num" aria-sort={sort === 'desc' ? 'descending' : 'ascending'}>
                    <button
                      type="button"
                      className="btn-link"
                      style={{ font: 'inherit', textTransform: 'inherit', letterSpacing: 'inherit', color: 'inherit' }}
                      onClick={() => setSort(sort === 'desc' ? 'asc' : 'desc')}
                    >
                      Balance <span aria-hidden="true">{sort === 'desc' ? '↓' : '↑'}</span>
                      <span className="visually-hidden">, sort {sort === 'desc' ? 'smallest' : 'largest'} first</span>
                    </button>
                  </th>
                  <th scope="col" className="num">
                    Not invoiced
                  </th>
                  <th scope="col" className="num">
                    Open disputes
                  </th>
                  <th scope="col">Fees</th>
                  <th scope="col" className="num">
                    Lead fee
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.partner_id}>
                    <td>
                      <Link to={detailPath(r)} className="rowlink">
                        {r.display_name}
                      </Link>
                    </td>
                    <td className="num">
                      <Amount cents={r.balance_cents} />
                    </td>
                    <td className="num">{formatCents(r.uninvoiced_cents)}</td>
                    <td className="num">{r.open_disputes ? <span className="tag warn">{r.open_disputes}</span> : 0}</td>
                    <td>{r.fees_enabled ? <span className="tag ok">On</span> : <span className="tag">Off</span>}</td>
                    <td className="num">{formatCents(r.lead_fee_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
